import { useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useAvatarPanel } from './AvatarPanel.context';
import type { VoiceMode } from './AvatarPanel.context';

type Props = {
  /** When false, the Voice option is hidden (feature-flag off). */
  showVoiceOption: boolean;
};

/**
 * VoiceModeDropdown — replaces the Voice Mode switch in the Digital Twin chat.
 *
 * Options:
 *  - Video (default) — avatar session active, user types text, TTS drives avatar speech.
 *  - Voice           — avatar session active, mic input enabled.
 *  - Text            — avatar session terminated, plain chat without TTS.
 *
 * When showVoiceOption is false (chatVoiceMode feature flag off), only
 * Video and Text are shown.
 *
 * Mode is stored in AvatarPanelContext (owned by DigitalTwinScreen) so that
 * DigitalTwinScreen can conditionally mount/unmount LiveAvatarSessionManager.
 */
export const VoiceModeDropdown = ({ showVoiceOption }: Props) => {
  const voicemode = useContext(VoiceModeContext);
  const { onClickVoiceMode } = useContext(InputControlsContext);
  const { mode, setMode } = useAvatarPanel();

  // Guard: if the Voice feature flag is off but mode is somehow 'voice',
  // fall back to 'video' to keep the Select value within its rendered items.
  const currentMode: VoiceMode = mode === 'voice' && !showVoiceOption ? 'video' : mode;

  const handleChange = useCallback((e: SelectChangeEvent<VoiceMode>) => {
    const next = e.target.value as VoiceMode;

    switch (next) {
      case 'video':
        // Video: avatar session active, user types text, TTS drives avatar speech.
        if (voicemode.enabled) onClickVoiceMode();
        setMode('video');
        break;
      case 'voice':
        // Voice: avatar session active, mic input enabled.
        if (!voicemode.enabled) onClickVoiceMode();
        setMode('voice');
        break;
      case 'text':
        // Text: end avatar session, plain chat without TTS.
        if (voicemode.enabled) onClickVoiceMode();
        setMode('text');
        break;
    }
  }, [voicemode.enabled, onClickVoiceMode, setMode]);

  return (
    <StyledSelect
      value={currentMode}
      onChange={handleChange}
      variant="outlined"
      size="small"
      renderValue={value => <SelectedValue mode={value} />}
    >
      <StyledMenuItem value="video">
      <MenuIcon><VideocamOutlinedIcon sx={{ fontSize: '1.35rem' }} /></MenuIcon>
        Video
      </StyledMenuItem>

      <StyledMenuItem value="text">
        <MenuIcon><ChatBubbleOutlineIcon fontSize="small" /></MenuIcon>
        Text
      </StyledMenuItem>

      {showVoiceOption && (
        <StyledMenuItem value="voice">
          <MenuIcon><VolumeUpOutlinedIcon fontSize="small" /></MenuIcon>
          Voice
        </StyledMenuItem>
      )}
    </StyledSelect>
  );
};

/** The compact trigger shown when the dropdown is closed. */
const SelectedValue = ({ mode }: { mode: VoiceMode }) => {
  const Icon = modeIcon(mode);
  const label = mode.charAt(0).toUpperCase() + mode.slice(1);
  return (
    <SelectedContent>
      <Icon fontSize="small" />
      <span>{label}</span>
    </SelectedContent>
  );
};

function modeIcon(mode: VoiceMode) {
  if (mode === 'video') return VideocamOutlinedIcon;
  if (mode === 'voice') return VolumeUpOutlinedIcon;
  return ChatBubbleOutlineIcon;
}


const StyledSelect = styled(Select<VoiceMode>)({
  height: 28,
  fontSize: 13,
  color: '#555',
  background: 'transparent',

  '& fieldset': {
    borderColor: '#ddd !important',
    borderRadius: 6,
  },

  '&:hover fieldset': {
    borderColor: '#aaa !important',
  },

  '& .MuiSelect-select': {
    padding: '2px 28px 2px 8px !important',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
});

const SelectedContent = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 1;
`;

const StyledMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 6px 14px;
`;

const MenuIcon = styled.span`
  display: flex;
  align-items: center;
  color: #555;
`;
